import { z } from 'zod'

import type { StaffRole, StoreRole } from '@/features/auth'

/**
 * Store-scoped delivery credentials — the domain half of
 * `vms-backend/docs/fe-order-ui-changes.md` §7.
 *
 * The one rule the whole module is built around: **a stored secret is never
 * readable again.** The backend returns only a per-field masked tail, so the FE
 * has no "current value" to prefill — editing always means typing a new value
 * into an empty field, and this file's types make that structural rather than a
 * habit (there is no plaintext credential anywhere on `DeliveryChannel`).
 */

/** Wire values, uppercase, and also the URL segment (§7.2). */
export const DELIVERY_PROVIDERS = ['LALAMOVE', 'AHAMOVE', 'SM'] as const
export type DeliveryProvider = (typeof DELIVERY_PROVIDERS)[number]
export const deliveryProviderSchema = z.enum(DELIVERY_PROVIDERS)

/**
 * Brand names, identical in every locale — a map so a new provider is a build error.
 *
 * `SM` is the wire code the backend uses for GreenSM, whose consumer-facing
 * brand in Vietnam is Xanh SM. The code is not shown anywhere; a cashier looking
 * for the channel is looking for the name on the scooter.
 */
export const DELIVERY_PROVIDER_NAMES: Record<DeliveryProvider, string> = {
  LALAMOVE: 'Lalamove',
  AHAMOVE: 'Ahamove',
  SM: 'Xanh SM',
}

export const CREDENTIAL_FIELDS = ['apiKey', 'apiSecret', 'accountId', 'webhookToken'] as const
export type CredentialFieldName = (typeof CREDENTIAL_FIELDS)[number]

/** Last four characters per field, or `null` where nothing is stored. */
export type MaskedTails = Partial<Record<CredentialFieldName, string | null>>

export type DeliveryChannel = {
  provider: DeliveryProvider
  /** This store has saved credentials for the channel. */
  configured: boolean
  /** The store's on/off switch. Off keeps the credentials (§7.2 rule 3). */
  isActive: boolean
  masked: MaskedTails
  updatedAt: Date | null
}

/**
 * What the card shows as its state.
 *
 * `unconfigured` deliberately does **not** mean "this channel cannot quote" —
 * §7.1 is explicit that a store with no credentials falls back to the system's
 * shared account. It means "this store is not billing its own account", which
 * is what the copy has to say.
 */
export type DeliveryChannelStatus = 'unconfigured' | 'active' | 'inactive'

export function channelStatus(channel: DeliveryChannel): DeliveryChannelStatus {
  if (!channel.configured) return 'unconfigured'
  return channel.isActive ? 'active' : 'inactive'
}

/* -------------------------------------------------------------------------- */
/* Credential forms                                                            */
/* -------------------------------------------------------------------------- */

export type CredentialField = {
  name: CredentialFieldName
  /**
   * `secret` masks while typing and never round-trips; `tel` is a phone number,
   * which is not a secret and stays readable so a typo is visible before saving.
   */
  kind: 'secret' | 'tel'
  optional?: true
}

/**
 * The per-provider form, as data (§7.2's required/optional table). A third
 * channel is a row here plus its labels in `strings.ts` — not a second form.
 */
export const PROVIDER_FIELDS: Record<DeliveryProvider, readonly CredentialField[]> = {
  LALAMOVE: [
    { name: 'apiKey', kind: 'secret' },
    { name: 'apiSecret', kind: 'secret' },
    { name: 'webhookToken', kind: 'secret', optional: true },
  ],
  AHAMOVE: [
    { name: 'apiKey', kind: 'secret' },
    { name: 'accountId', kind: 'tel' },
    { name: 'webhookToken', kind: 'secret', optional: true },
  ],
  // Xanh SM reuses apiKey/apiSecret for what its Developer Portal calls Client
  // ID and Client Secret — the labels say so, since nobody pasting credentials
  // should have to work that mapping out.
  //
  // `webhookToken` is the one required here and optional on the other two, and
  // the difference is not cosmetic: for SM it is the HMAC signing key, and it is
  // that endpoint's *entire* authentication. Without it every callback is
  // refused with 401 — so the channel would dispatch riders perfectly well and
  // then never update a status again, leaving a till stuck on "finding a driver"
  // for a delivery that already arrived. Lalamove signs its callbacks with the
  // API secret and Ahamove falls back to the deployment token, so neither has
  // this failure mode.
  SM: [
    { name: 'apiKey', kind: 'secret' },
    { name: 'apiSecret', kind: 'secret' },
    { name: 'webhookToken', kind: 'secret' },
  ],
}

/**
 * Loose on purpose, and deliberately the same rule the backend applies
 * (`^\+?\d{8,15}$`): Ahamove is the real authority on whether a number is
 * theirs, so this only catches input that could not be a phone number at all.
 * An obvious typo is answered instantly instead of after a live round trip;
 * anything subtler comes back as `invalid_mobile`.
 */
const ACCOUNT_MOBILE = /^\+?\d{8,15}$/u

/**
 * Messages are **codes, not sentences**: this module is locale-free, and the
 * card maps the code through `strings.ts`. A sentence here would be a Vietnamese
 * string a Korean operator could not read.
 */
const requiredText = z.string().trim().min(1, 'required')

export const credentialSchemas = {
  LALAMOVE: z.object({
    apiKey: requiredText,
    apiSecret: requiredText,
    webhookToken: z.string().trim(),
  }),
  AHAMOVE: z.object({
    apiKey: requiredText,
    accountId: z.string().trim().min(1, 'required').regex(ACCOUNT_MOBILE, 'mobile'),
    webhookToken: z.string().trim(),
  }),
  SM: z.object({
    apiKey: requiredText,
    apiSecret: requiredText,
    // Required, unlike the other two — see the note on PROVIDER_FIELDS.SM.
    // Caught here rather than at dispatch time because the symptom is silent:
    // deliveries would be booked and then never update.
    webhookToken: requiredText,
  }),
} satisfies Record<DeliveryProvider, z.ZodType>

export type CredentialValues = Record<CredentialFieldName, string>
/** Field → error *code*, for the card to localize. Empty object means valid. */
export type CredentialIssues = Partial<Record<CredentialFieldName, string>>

export const emptyCredentials = (): CredentialValues => ({
  apiKey: '',
  apiSecret: '',
  accountId: '',
  webhookToken: '',
})

/**
 * Validates a **first-time setup**, where every required field must be present.
 *
 * A rotation is a different shape: §7.2 rule 2 says an omitted field keeps its
 * stored value, so replacing only the secret is a legal request this function
 * would reject. `configured` tells the two apart — see `validateForSave`.
 */
export function validateCredentials(
  provider: DeliveryProvider,
  values: CredentialValues,
): CredentialIssues {
  const result = credentialSchemas[provider].safeParse(values)
  if (result.success) return {}

  const issues: CredentialIssues = {}
  for (const issue of result.error.issues) {
    const field = issue.path[0]
    // First issue per field wins — `required` before `mobile` on an empty box,
    // which is the more useful of the two.
    if (typeof field === 'string' && issues[field as CredentialFieldName] === undefined) {
      issues[field as CredentialFieldName] = issue.message
    }
  }
  return issues
}

/**
 * What the Save button checks.
 *
 * Already configured → any subset passes, because blank means "keep what is
 * stored". A filled `accountId` is still format-checked: a typo there is worth
 * catching before the round trip.
 */
export function validateForSave(
  provider: DeliveryProvider,
  values: CredentialValues,
  configured: boolean,
): CredentialIssues {
  if (!configured) return validateCredentials(provider, values)

  const issues: CredentialIssues = {}
  const accountId = values.accountId.trim()
  if (accountId !== '' && !ACCOUNT_MOBILE.test(accountId)) issues.accountId = 'mobile'
  return issues
}

/** True when a rotation form has something in it — the FE's `nothing_to_update` guard. */
export function hasAnyCredential(provider: DeliveryProvider, values: CredentialValues): boolean {
  return PROVIDER_FIELDS[provider].some((field) => values[field.name].trim() !== '')
}

/**
 * Only the fields this provider has, trimmed, with blanks dropped.
 *
 * Dropping blanks is the contract, not tidiness: §7.2 rule 2 makes an empty
 * string mean "unchanged", so sending one to clear a value silently does
 * nothing. There is no way to blank a stored field — removing the whole channel
 * is the operation for that.
 */
export function credentialPayload(
  provider: DeliveryProvider,
  values: CredentialValues,
): Partial<Record<CredentialFieldName, string>> {
  const payload: Partial<Record<CredentialFieldName, string>> = {}
  for (const field of PROVIDER_FIELDS[provider]) {
    const value = values[field.name].trim()
    if (value !== '') payload[field.name] = value
  }
  return payload
}

/* -------------------------------------------------------------------------- */
/* Masking                                                                     */
/* -------------------------------------------------------------------------- */

/** The dots the card draws in front of the tail. Never part of the stored data. */
export const MASK_DOTS = '····'

/**
 * Normalise whatever the backend calls "masked" down to the tail this app draws.
 *
 * The server sends `····9911`, and an empty string for "not set". Tolerant of
 * the punctuation because the dots are a rendering choice on both sides, and a
 * settings card is not worth an incident over which bullet character was used.
 */
export function maskTail(masked: string | null | undefined): string | null {
  if (masked == null) return null
  const alphanumeric = masked.replace(/[^\p{L}\p{N}]/gu, '')
  if (alphanumeric === '') return null
  return alphanumeric.slice(-4)
}

/* -------------------------------------------------------------------------- */
/* Permission                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Who may write a store's delivery credentials: HQ anywhere, a store manager in
 * their own store, nobody else (§7.0).
 *
 * **`role` is checked before `store_role`, and that order is load-bearing**: an
 * HQ account reports `store_role: ""` because it belongs to no store, so a gate
 * that read the rank first would lock HQ out of the screen it has the most
 * rights on. Everything that is not a manager falls through to deny — which is
 * also what an unknown or missing rank gets.
 */
export function canEditDeliveryChannels(
  role: StaffRole,
  storeRole: StoreRole | null | undefined,
): boolean {
  if (role === 'HQADMIN') return true
  return storeRole === 'MANAGER'
}
